'use client';

import type { ReactNode } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronUp, Eye, EyeOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { MockBoard } from '../data/mock-boards';
import {
  BoardMetadataSnapshot,
  BOARD_METADATA_GLASS,
  type BoardCreatorPin,
} from './board-metadata-snapshot';
import type { SkyboxTheme } from '../theming/skyboxes/types';

export type { BoardCreatorPin };

interface PinnedTitlePanelProps {
  board: MockBoard;
  creator?: BoardCreatorPin | null;
  hidden: boolean;
  onToggleHidden: () => void;
  className?: string;
  skybox?: SkyboxTheme | null;
  /** While the active board's grid page is still loading, the static name
   *  from `useLobbyBoards.byId` renders instantly but the dynamic creator
   *  pill + description + socials are replaced with skeleton bars — same
   *  signal `isBoardDataLoading` gives every other panel on switch. */
  loading?: boolean;
  /** Optional creator-only Edit Panel rendered inside the same rounded
   *  glass shell as the metadata snapshot (forwarded to its `footer`
   *  slot). Mirrors the Board Yield card's nested-edit pattern. */
  editPanel?: ReactNode;
}

export function PinnedTitlePanel({
  board,
  creator = null,
  hidden,
  onToggleHidden,
  className,
  skybox: _skybox = null,
  loading = false,
  editPanel = null,
}: PinnedTitlePanelProps) {
  return (
    <div className={cn('flex flex-col items-start gap-1.5 pointer-events-auto', className)}>
      <AnimatePresence mode="wait" initial={false}>
        {!hidden ? (
          <motion.div
            key="open"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.22 }}
            className="flex flex-col gap-1.5"
          >
            <BoardMetadataSnapshot
              board={board}
              creator={creator}
              loading={loading}
              footer={editPanel}
              headerTrailing={
                <button
                  type="button"
                  onClick={onToggleHidden}
                  title="Hide title"
                  className="h-5 w-5 grid place-items-center rounded-[2px] text-zinc-400 hover:text-amber-100"
                >
                  <EyeOff className="h-3 w-3" />
                </button>
              }
            />
          </motion.div>
        ) : (
          <motion.button
            key="closed"
            type="button"
            onClick={onToggleHidden}
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
            className={cn(
              'flex items-center gap-1.5 h-7 px-2 rounded-md border text-[10px] font-mono tracking-[0.18em] uppercase',
              'text-zinc-100 hover:text-amber-100 [text-shadow:0_1px_2px_rgba(0,0,0,0.65)]',
              BOARD_METADATA_GLASS,
            )}
            title="Show title"
          >
            <Eye className="h-3 w-3" />
            {board.name}
            <ChevronUp className="h-3 w-3 rotate-180 opacity-60" />
          </motion.button>
        )}
      </AnimatePresence>
    </div>
  );
}
